import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";

// Back-office billing administration: manual/lifetime subscriptions,
// suspend/reactivate, promo & trial codes, per-org entitlement overrides and
// the billing audit trail. All endpoints are operator-only.

const initialState = {
  promoCodes: [],
  auditEvents: [],
  entitlementOverride: null,
  state: "idle",
  error: null,
  message: null
};

const rejectWith = (err, data) =>
  Promise.reject(err.message ? err.message : data?.message || "request failed");

// ── Subscription lifecycle ────────────────────────────────────────────────

export const assignManualSubscription = createAsyncThunk(
  "api/billing-admin/assign-subscription",
  async ({ orgId, planId, lifetime = false, currentPeriodEnd = 0 }) => {
    let data;
    try {
      const response = await axios.post(`/subscriptions/operator`, {
        orgId,
        planId,
        lifetime,
        currentPeriodEnd
      });
      data = await response.data;
      if (response.status === 200) return data;
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWith(err, data);
    }
  }
);

export const suspendSubscription = createAsyncThunk(
  "api/billing-admin/suspend-subscription",
  async ({ orgId, subscriptionId }) => {
    let data;
    try {
      const response = await axios.put(
        `/subscriptions/operator/${orgId}/${subscriptionId}/suspend`
      );
      data = await response.data;
      if (response.status === 200) return data;
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWith(err, data);
    }
  }
);

export const reactivateSubscription = createAsyncThunk(
  "api/billing-admin/reactivate-subscription",
  async ({ orgId, subscriptionId }) => {
    let data;
    try {
      const response = await axios.put(
        `/subscriptions/operator/${orgId}/${subscriptionId}/reactivate`
      );
      data = await response.data;
      if (response.status === 200) return data;
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWith(err, data);
    }
  }
);

// ── Promo & trial codes ───────────────────────────────────────────────────

export const getPromoCodes = createAsyncThunk("api/billing-admin/promocodes", async () => {
  let data;
  try {
    const response = await axios.get(`/promocodes/operator`);
    data = await response.data;
    if (response.status === 200) return data;
    throw new Error(response.statusText);
  } catch (err) {
    return rejectWith(err, data);
  }
});

export const createPromoCode = createAsyncThunk(
  "api/billing-admin/create-promocode",
  async (values, thunkApi) => {
    let data;
    try {
      const response = await axios.post(`/promocodes/operator`, { ...values });
      data = await response.data;
      if (response.status === 200) {
        thunkApi.dispatch(getPromoCodes());
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWith(err, data);
    }
  }
);

export const updatePromoCode = createAsyncThunk(
  "api/billing-admin/update-promocode",
  async ({ code, values }, thunkApi) => {
    let data;
    try {
      const response = await axios.put(`/promocodes/operator/${code}`, { ...values });
      data = await response.data;
      if (response.status === 200) {
        thunkApi.dispatch(getPromoCodes());
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWith(err, data);
    }
  }
);

export const deletePromoCode = createAsyncThunk(
  "api/billing-admin/delete-promocode",
  async (code, thunkApi) => {
    let data;
    try {
      const response = await axios.delete(`/promocodes/operator/${code}`);
      data = await response.data;
      if (response.status === 200) {
        thunkApi.dispatch(getPromoCodes());
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWith(err, data);
    }
  }
);

// ── Entitlement overrides ─────────────────────────────────────────────────

export const getEntitlementOverride = createAsyncThunk(
  "api/billing-admin/get-entitlements",
  async (orgId) => {
    let data;
    try {
      const response = await axios.get(`/entitlements/operator/${orgId}`);
      data = await response.data;
      if (response.status === 200) return data;
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWith(err, data);
    }
  }
);

export const setEntitlementOverride = createAsyncThunk(
  "api/billing-admin/set-entitlements",
  async ({ orgId, values }, thunkApi) => {
    let data;
    try {
      const response = await axios.put(`/entitlements/operator/${orgId}`, { ...values });
      data = await response.data;
      if (response.status === 200) {
        thunkApi.dispatch(getEntitlementOverride(orgId));
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWith(err, data);
    }
  }
);

export const clearEntitlementOverride = createAsyncThunk(
  "api/billing-admin/clear-entitlements",
  async (orgId, thunkApi) => {
    let data;
    try {
      const response = await axios.delete(`/entitlements/operator/${orgId}`);
      data = await response.data;
      if (response.status === 200) {
        thunkApi.dispatch(getEntitlementOverride(orgId));
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWith(err, data);
    }
  }
);

// ── Audit trail ───────────────────────────────────────────────────────────

export const getAuditEvents = createAsyncThunk(
  "api/billing-admin/audit",
  async ({ org = "", resourceType = "", limit = 200 } = {}) => {
    let data;
    try {
      const params = new URLSearchParams();
      if (org) params.set("org", org);
      if (resourceType) params.set("resourceType", resourceType);
      if (limit) params.set("limit", limit);
      const response = await axios.get(`/audit/operator?${params.toString()}`);
      data = await response.data;
      if (response.status === 200) return data;
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWith(err, data);
    }
  }
);

export const billingAdminSlice = createSlice({
  name: "billingAdmin",
  initialState,
  reducers: {
    clearBillingAdminError: (state) => {
      state.error = null;
      state.message = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPromoCodes.fulfilled, (state, action) => {
        state.promoCodes = action.payload || [];
        state.state = "success";
      })
      .addCase(getAuditEvents.fulfilled, (state, action) => {
        state.auditEvents = action.payload || [];
        state.state = "success";
      })
      .addCase(getEntitlementOverride.fulfilled, (state, action) => {
        state.entitlementOverride = action.payload;
        state.state = "success";
      })
      .addCase(assignManualSubscription.fulfilled, (state) => {
        state.message = "Subscription assigned successfully";
        state.state = "success";
      })
      .addCase(suspendSubscription.fulfilled, (state) => {
        state.message = "Subscription suspended";
        state.state = "success";
      })
      .addCase(reactivateSubscription.fulfilled, (state) => {
        state.message = "Subscription reactivated";
        state.state = "success";
      })
      .addMatcher(
        (action) => action.type.startsWith("api/billing-admin/") && action.type.endsWith("/pending"),
        (state) => {
          state.error = null;
          state.state = "loading";
        }
      )
      .addMatcher(
        (action) => action.type.startsWith("api/billing-admin/") && action.type.endsWith("/rejected"),
        (state, action) => {
          state.error = action.error?.message || "request failed";
          state.state = "error";
        }
      );
  }
});

export const { clearBillingAdminError } = billingAdminSlice.actions;

export default billingAdminSlice.reducer;
